import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQueryWithReauth';
import type {
  PasskeyListResponse,
  PasskeyStoreRequest,
  PasskeyStoreResponse,
  PasskeyAuthenticateRequest,
  PasskeyRenameRequest,
  PasskeyAuthOptionsResponse,
  PasskeyRegisterOptionsResponse,
} from './models';
import type { UserLoginResponse } from '../../models-to-replace/auth';

const PASSKEYS_TAG = 'passkeys';

export const passkeysApi = createApi({
  reducerPath: 'passkeys',
  baseQuery: baseQueryWithReauth,
  tagTypes: [PASSKEYS_TAG],
  endpoints: (build) => ({
    // Authentication flow (public, no auth required)
    getPasskeyAuthOptions: build.query<PasskeyAuthOptionsResponse, void>({
      query: () => ({
        url: '/passkeys/authentication-options',
        headers: { Accept: 'application/json' },
      }),
    }),
    passkeyAuthenticate: build.mutation<UserLoginResponse, PasskeyAuthenticateRequest>({
      query: (body) => ({
        url: '/passkeys/authenticate',
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      }),
    }),

    // Registration flow (authenticated, for Settings)
    getPasskeyRegisterOptions: build.query<PasskeyRegisterOptionsResponse, void>({
      query: () => ({
        url: '/user/passkeys/generate-options',
        headers: { Accept: 'application/json' },
      }),
    }),
    storePasskey: build.mutation<PasskeyStoreResponse, PasskeyStoreRequest>({
      query: (body) => ({
        url: '/user/passkeys',
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: PASSKEYS_TAG, id: 'LIST' }],
    }),

    // Management (authenticated, for Settings)
    getPasskeys: build.query<PasskeyListResponse, void>({
      query: () => ({
        url: '/user/passkeys',
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: PASSKEYS_TAG, id: 'LIST' }],
    }),
    renamePasskey: build.mutation<PasskeyStoreResponse, PasskeyRenameRequest>({
      query: ({ id, name }) => ({
        url: `/user/passkeys/${id}`,
        method: 'PATCH',
        body: { name },
        headers: { Accept: 'application/json' },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            passkeysApi.util.updateQueryData('getPasskeys', undefined, (draft) => {
              const index = draft.data.findIndex((p) => p.id === response.data.id);
              if (index !== -1) {
                draft.data[index] = response.data;
              }
            }),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    deletePasskey: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/user/passkeys/${id}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: PASSKEYS_TAG, id: 'LIST' }],
    }),
  }),
});

export const {
  useLazyGetPasskeyAuthOptionsQuery,
  usePasskeyAuthenticateMutation,
  useLazyGetPasskeyRegisterOptionsQuery,
  useStorePasskeyMutation,
  useGetPasskeysQuery,
  useRenamePasskeyMutation,
  useDeletePasskeyMutation,
} = passkeysApi;
